import apiClient from "./api"

export interface SubscriptionPlan {
    _id: string
    name: string
    nameAr?: string
    pricing: {
        monthly: number
        yearly: number
    }
    features: string[]
    featuresAr?: string[]
    isActive: boolean
    price?: number // Deprecated, kept for backward compatibility if any old code uses it
    interval?: 'monthly' | 'yearly' // Deprecated
}

export interface Subscriber {
    _id: string
    restaurant: { _id: string, name: string, logo?: string, email?: string }
    plan: SubscriptionPlan
    status: 'active' | 'expired' | 'cancelled' | 'trial' | 'free'
    startDate: string
    endDate: string
    billingCycle?: 'monthly' | 'yearly'
    autoRenew: boolean
    paymentHistory?: PaymentRecord[]
}

export interface PaymentRecord {
    _id: string
    amount: number
    date: string
    status: 'pending' | 'confirmed' | 'failed'
    reference?: string
    invoiceUrl?: string
}

export interface EmailTemplate {
    _id: string
    name: string
    type: 'invoice' | 'receipt' | 'payment_confirmation' | 'reminder' | 'cancellation'
    subject: string
    body: string // HTML content
    variables: string[] // Available template variables
    isActive: boolean
    createdAt: string
    updatedAt: string
}

export const billingAPI = {
    // Plans
    getPlans: () => apiClient.get("/admin/subscription-plans"),
    createPlan: (data: Omit<SubscriptionPlan, "_id">) => apiClient.post("/admin/subscription-plans", data),
    updatePlan: (id: string, data: Partial<SubscriptionPlan>) => apiClient.put(`/admin/subscription-plans/${id}`, data),
    deletePlan: (id: string) => apiClient.delete(`/admin/subscription-plans/${id}`),

    // Subscribers
    getSubscribers: (params?: any) => apiClient.get("/admin/subscriptions", { params }),
    getSubscriber: (id: string) => apiClient.get(`/admin/subscriptions/${id}`),

    // Auto-enrollment
    enrollRestaurantOnFreePlan: (restaurantId: string) =>
        apiClient.post(`/admin/subscriptions/enroll-free`, { restaurantId }),

    checkAndEnrollUnsubscribed: () =>
        apiClient.post("/admin/subscriptions/auto-enroll-free"),

    // Actions
    assignPlan: (data: { restaurantId: string; planId: string; billingCycle?: string; startDate?: string; endDate?: string; status?: string }) =>
        apiClient.post("/admin/subscriptions/assign", data),

    updateSubscription: (subscriptionId: string, data: any) =>
        apiClient.put(`/admin/subscriptions/${subscriptionId}`, data),

    confirmPayment: (subscriptionId: string, paymentReference: string) =>
        apiClient.post(`/admin/subscriptions/${subscriptionId}/confirm-payment`, { paymentReference }),

    extendSubscription: (subscriptionId: string, days: number) =>
        apiClient.post(`/admin/subscriptions/${subscriptionId}/extend`, { days }),

    cancelSubscription: (subscriptionId: string) =>
        apiClient.post(`/admin/subscriptions/${subscriptionId}/cancel`),

    // Email Templates
    getEmailTemplates: (params?: any) => apiClient.get("/admin/email-templates", { params }),
    getEmailTemplate: (id: string) => apiClient.get(`/admin/email-templates/${id}`),
    createEmailTemplate: (data: Omit<EmailTemplate, "_id" | "createdAt" | "updatedAt">) =>
        apiClient.post("/admin/email-templates", data),
    updateEmailTemplate: (id: string, data: Partial<EmailTemplate>) =>
        apiClient.put(`/admin/email-templates/${id}`, data),
    deleteEmailTemplate: (id: string) => apiClient.delete(`/admin/email-templates/${id}`),
    sendTestEmailTemplate: (id: string, email: string) =>
        apiClient.post(`/admin/email-templates/${id}/test`, { email }),

    // Send emails using templates
    sendTemplatedEmail: (data: {
        restaurantId: string,
        templateId: string,
        variables: Record<string, string>
    }) => apiClient.post("/admin/email/send-templated", data),
}
